import React, { useCallback, useEffect, useState } from 'react'
import { Checkbox } from '@mui/material'

interface WithId {
  id: string
}

interface CheckboxSelectAllProps<T extends WithId> {
  data: T[]
  selected: T[]
  setSelected: React.Dispatch<React.SetStateAction<T[]>>
}

const CheckboxSelectAll = <T extends WithId>({ data, selected, setSelected }: CheckboxSelectAllProps<T>) => {
  const [isChecked, setIsChecked] = useState<boolean>(false)
  const [isIndeterminate, setIsIndeterminate] = useState<boolean>(false)

  const isInSelected = useCallback(
    (row: T): boolean => {
      return selected.some(selectedRow => selectedRow.id === row.id)
    },
    [selected]
  )

  const isDataInSelected = useCallback((): boolean => {
    return data.every(row => isInSelected(row))
  }, [data, isInSelected])

  const isSomeDataInSelected = useCallback((): boolean => {
    return data.some(row => isInSelected(row))
  }, [data, isInSelected])

  useEffect(() => {
    if (data.length !== 0) {
      setIsChecked(isDataInSelected() || isSomeDataInSelected())
      setIsIndeterminate(!isDataInSelected() && isSomeDataInSelected())
    } else {
      setIsChecked(false)
      setIsIndeterminate(false)
    }
  }, [data, selected, isDataInSelected, isSomeDataInSelected])

  const isSelected = (entry: T) => {
    let indexes = selected.map(entry => entry.id)
    return indexes.indexOf(entry.id) !== -1
  }

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setIsChecked(true)
      const newData = data.filter(row => !isSelected(row))
      setSelected(prevSelected => [...prevSelected, ...newData])
      return
    }
    setIsChecked(false)
    setSelected(prevSelected => prevSelected.filter(row => !data.some(selectedRow => selectedRow.id === row.id)))
  }

  return (
    <Checkbox
      id={'select-all-checkbox'}
      checked={isChecked}
      indeterminate={isIndeterminate}
      onChange={handleSelectAllClick}
      inputProps={{ 'aria-label': 'select all checkbox' }}
      disabled={data.length === 0}
    />
  )
}

export default CheckboxSelectAll
